import { useGet } from '@/query/fetchHooks';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext, Fragment } from 'react';
import {
  PersonAvatar,
  PageHeader,
  Button,
  Tag,
  InputText,
  InputNumber,
  TEOLoader,
  IconButton,
  Checkbox,
  Dropdown,
} from '@teo/components';
import {
  Time,
  ChevronRight,
  Search,
  TaskCheckmarkFilled,
  CloseCircleFilled,
  ChevronDownFilled,
  MoreVertical,
  Download,
} from '@teo/components/icons';
import {
  parseRole,
  parseFilter,
  useMobile,
  nthLastIndexOf,
  downloadLink,
} from '@/util.js';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import { useQueryClient } from '@tanstack/react-query';
import {
  SparklesIcon,
  ExpiredIcon,
} from '@/components/Competences/SparklesIcon';
import AssignProfiles from '@/components/Competencies/Profiles/AssignProfiles';
import { AddCompetenceUser } from '@/components/Competencies/AddCompetenceUser';
import PaginationNew from '@/components/DataTable/PaginationNew';
import { Listbox, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { BreadCrumbsDetails } from '@/components/BreadCrumbsDetails';
import { NavContext } from '@/components/NavContext';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';
import { axios } from '@/axios';
import { config } from '@/config';
import EmptyTable from '@/components/DataTable/EmptyTable';
import { NotificationContext } from '@/components/Contexts/NotificationContext';

const formatProfilePercent = (value: any) => {
  const number = Math.max(0, Math.min(1, Number(value || 0)));
  return `${Math.round(number * 100)}%`;
};

const ProfileProgress = ({ value, compact = false }: any) => {
  const progress = Math.max(0, Math.min(1, Number(value || 0)));

  return (
    <div
      className={`flex items-center gap-2 ${
        compact ? 'min-w-[120px]' : 'min-w-[180px]'
      }`}
    >
      <div className="h-2 flex-1 rounded-full bg-[#E9E9EB]">
        <div
          className={`h-2 rounded-full ${
            progress >= 0.8 ? 'bg-[#24A148]' : 'bg-[#F1C21B]'
          }`}
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <span className="min-w-[38px] text-right text-xs font-semibold tabular-nums">
        {formatProfilePercent(progress)}
      </span>
    </div>
  );
};

const CompetenceActionMenu = ({ competence, onAction }: any) => {
  const { t } = useTranslation();

  return (
    <Dropdown>
      <Dropdown.Trigger>
        <IconButton
          Icon={MoreVertical}
          variant="ghost"
          className="!text-grey-06"
          aria-label={t('pages.members.more')}
        />
      </Dropdown.Trigger>
      <Dropdown.Content
        style={{ top: '24px', right: '0', left: 'auto', minWidth: '160px' }}
      >
        <Dropdown.Actions>
          <Dropdown.Button onClick={() => onAction(competence, 'assign')}>
            <span className="px-2 py-1 text-sm">
              {t('pages.follow_up_users.competence_assign_action')}
            </span>
          </Dropdown.Button>
          <Dropdown.Button onClick={() => onAction(competence, 'revoke')}>
            <span className="px-2 py-1 text-sm">
              {t('pages.follow_up_users.competence_revoke_action')}
            </span>
          </Dropdown.Button>
          <Dropdown.Button onClick={() => onAction(competence, 'history')}>
            <span className="px-2 py-1 text-sm">
              {t('pages.follow_up_users.competence_history_tab')}
            </span>
          </Dropdown.Button>
        </Dropdown.Actions>
      </Dropdown.Content>
    </Dropdown>
  );
};

function ProfileCompetenceRows({
  organisationId,
  professionId,
  userId,
  onCompetenceAction,
}: any) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [competences, setCompetences] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [count, setCount] = useState(10);
  const [maxPage, setMaxPage] = useState(1);
  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[0]);

  const [loaded, setLoaded] = useState(true);
  useGet('/studentprofessioncompetencedetails', {
    enabled: !!organisationId && !!userId && !!professionId,
    organisationId,
    userId,
    professionId,
    sort: 'description',
    count,
    offset,
    onSuccess: (result: any) => {
      setLoaded(true);
      setResult(result || {});
    },
  });

  useEffect(() => {
    if (result) {
      setCompetences(result?.data || {});
      const contentRange = result?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');
      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
    }
  }, [count, result]);

  const page = Math.floor(offset / count) + 1;

  useEffect(() => {
    setLoaded(false);
  }, [count, offset]);

  if (competences === null) {
    return (
      <div className="border-t border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
        {t('pages.follow_up_users.profile_competences_loading')}
      </div>
    );
  }

  if (!competences?.length) {
    return (
      <div className="border-t border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
        {t('pages.follow_up_users.profile_competences_empty')}
      </div>
    );
  }

  return (
    <>
      {competences?.length > 0 && (
        <>
          <div className="relative border-t border-grey-02">
            {!loaded && (
              <div className="absolute inset-0 z-[101] flex bg-white/75">
                <div className="m-auto">
                  <TEOLoader
                    loaderSrc="/images/TEOloader.gif"
                    className="w-[120px]"
                  />
                </div>
              </div>
            )}
            {competences.map((competence: any) => {
              const isCompleted = Boolean(competence.isCompleted);
              const isExempted = competence.assignmentMethod === 'exemption';
              const isDenied = competence.assignmentMethod === 'denied';
              const isRevoked = competence.assignmentMethod === 'revoked';
              const hasExpired = competence.hasExpired;

              return (
                <div
                  key={competence.id}
                  className="grid grid-cols-[36px_1fr] gap-3 border-b border-grey-02  px-4 py-3.5 last:border-b-0 md:grid-cols-[36px_1fr_150px_150px_50px]"
                >
                  <span className="col-start-1 row-start-1 mt-0.5 flex h-8 w-8 items-center justify-center  md:col-start-auto md:row-start-auto">
                    {hasExpired ? (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#FB8C00] text-white">
                        <ExpiredIcon className="h-4 w-4" />
                      </span>
                    ) : isDenied || isRevoked ? (
                      <CloseCircleFilled className="h-8 w-8 text-error-05" />
                    ) : isExempted ? (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary-05 text-white">
                        <SparklesIcon className="h-4 w-4" />
                      </span>
                    ) : isCompleted ? (
                      <TaskCheckmarkFilled className="h-8 w-8 text-success-05" />
                    ) : (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-grey-03 text-white">
                        <Time className="h-4 w-4 text-grey-06" />
                      </span>
                    )}
                  </span>
                  <div className="col-start-2 row-start-1 min-w-0 md:col-start-auto md:row-start-auto">
                    <div className="truncate text-sm font-semibold text-[#231F20]">
                      <div className="flex items-center gap-2 py-1 text-sm leading-none text-grey-08">
                        {competence?.competenceCategoryColorCode && (
                          <span
                            style={{
                              backgroundColor:
                                competence?.competenceCategoryColorCode,
                            }}
                            className={`h-1.5 w-1.5 min-w-1.5 rounded-full`}
                          ></span>
                        )}
                        <p className="leading-[1.3]">{competence?.name}</p>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-5 text-[#7D7D81]">
                      {competence.code && <span>{competence.code}</span>}
                      <span>
                        {competence.isMandatory
                          ? t(
                              'pages.follow_up_users.profile_competence_mandatory'
                            )
                          : t(
                              'pages.follow_up_users.profile_competence_optional'
                            )}
                      </span>
                    </div>
                  </div>
                  <div className="col-start-2 flex items-center md:col-start-auto">
                    <ProfileProgress value={competence.progress} compact />
                  </div>
                  <div className="col-start-2 flex items-center md:col-start-auto md:justify-end">
                    <Tag
                      size="sm"
                      title={
                        hasExpired
                          ? t('pages.follow_up_users.profile_status_expired')
                          : isRevoked
                          ? t('pages.follow_up_users.competence_status_revoked')
                          : isDenied
                          ? t('pages.follow_up_users.competence_status_denied')
                          : isExempted
                          ? t(
                              'pages.follow_up_users.competence_status_exempted'
                            )
                          : isCompleted
                          ? t(
                              'pages.follow_up_users.competence_status_completed'
                            )
                          : competence.progress === 0
                          ? t(
                              'pages.follow_up_users.competence_status_not_started'
                            )
                          : t(
                              'pages.follow_up_users.competence_status_not_completed'
                            )
                      }
                      variant={
                        hasExpired
                          ? 'error'
                          : isRevoked
                          ? 'error'
                          : isDenied
                          ? 'error'
                          : isExempted
                          ? 'primary'
                          : isCompleted
                          ? 'success'
                          : 'warning'
                      }
                    />
                  </div>
                  <div className="col-start-3 row-start-1 flex items-center md:col-start-auto md:row-start-auto md:justify-end">
                    <CompetenceActionMenu
                      competence={competence}
                      onAction={onCompetenceAction}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {maxPage > 1 && (
            <div className="flex w-full items-center justify-between bg-[#F6F6F7] px-4">
              <div>
                <PaginationNew
                  setOffset={setOffset}
                  offset={offset}
                  count={count}
                  page={page}
                  maxPage={maxPage}
                />
              </div>
              <div className="flex items-center">
                <div className="ml-2 hidden items-center gap-1 lg:flex">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.go_to_page')}
                  </p>
                  <InputNumber
                    mode="integer"
                    value={page}
                    onChange={(value: any) => {
                      if (value > 0) {
                        setOffset((value - 1) * count);
                      }
                    }}
                    min={1}
                    max={maxPage}
                    className="max-w-[50px]"
                  />
                </div>
                <div className="ml-2 flex items-center gap-1">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.show')}
                  </p>
                  <div>
                    <Listbox
                      value={showType}
                      onChange={(newShowType) => {
                        setShowType(newShowType);
                        setCount(newShowType?.value);
                        setOffset(
                          Math.floor(offset / newShowType?.value) *
                            newShowType?.value
                        );
                      }}
                    >
                      {({ open }) => (
                        <div>
                          <Listbox.Button
                            className={clsx(
                              'flex items-center justify-between',
                              'rounded-md border border-solid outline outline-2',
                              'min-h-10 w-full min-w-[70px] px-2 text-base',
                              {
                                'border-secondary-04 bg-white outline-secondary-01':
                                  open,
                                'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                  !open,
                              }
                            )}
                          >
                            <span className="mr-2 block truncate">
                              {!showType?.label ? '' : showType?.label}
                            </span>
                            <ChevronDownFilled
                              className={clsx(
                                'h-5 w-5',
                                'transition-transform',
                                open && 'rotate-180'
                              )}
                            />
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition-opacity"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="listbox relative z-10">
                              <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                {showOptions.map((option, optionIndex) => (
                                  <Listbox.Option
                                    key={optionIndex}
                                    className={({ active }) =>
                                      clsx(
                                        'rounded-md px-3 py-2 text-base text-black',
                                        active && 'bg-grey-transparent-02'
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected }) => (
                                      <span
                                        className={clsx(
                                          'block',
                                          selected && 'font-medium'
                                        )}
                                      >
                                        <span>{option?.label}</span>
                                      </span>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function UnProfileCompetenceRows({
  setOffset,
  offset,
  setCount,
  count,
  result,
  loadedUnProf,
  onCompetenceAction,
}: any) {
  const { t } = useTranslation();
  const [competences, setCompetences] = useState<any>(null);
  const [maxPage, setMaxPage] = useState(1);
  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[0]);

  useEffect(() => {
    if (result) {
      setCompetences(result?.data || {});
      const contentRange = result?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');
      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
    }
  }, [count, result]);

  const page = Math.floor(offset / count) + 1;

  if (competences === null) {
    return (
      <div className="border-t border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
        {t('pages.follow_up_users.profile_competences_loading')}
      </div>
    );
  }

  if (!competences?.length) {
    return (
      <div className="border-t border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
        {t('pages.follow_up_users.profile_competences_empty')}
      </div>
    );
  }

  return (
    <>
      {competences?.length > 0 && (
        <>
          <div className="relative border-t border-grey-02 bg-white">
            {!loadedUnProf && (
              <div className="absolute inset-0 z-[101] flex bg-white/75">
                <div className="m-auto">
                  <TEOLoader
                    loaderSrc="/images/TEOloader.gif"
                    className="w-[120px]"
                  />
                </div>
              </div>
            )}
            {competences.map((competence: any) => {
              const isCompleted = Boolean(competence.isCompleted);
              const isExempted = competence.assignmentMethod === 'exemption';
              const isDenied = competence.assignmentMethod === 'denied';
              const isRevoked = competence.assignmentMethod === 'revoked';
              const hasExpired = competence.hasExpired;

              return (
                <div
                  key={competence.id}
                  className="flex items-center gap-3 border-b border-grey-02 bg-white px-4 py-3.5 last:border-b-0"
                >
                  <span className="mt-0.5 flex h-8 w-8 items-center justify-center">
                    {hasExpired ? (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#FB8C00] text-white">
                        <ExpiredIcon className="h-4 w-4" />
                      </span>
                    ) : isDenied || isRevoked ? (
                      <CloseCircleFilled className="h-8 w-8 text-error-05" />
                    ) : isExempted ? (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary-05 text-white">
                        <SparklesIcon className="h-4 w-4" />
                      </span>
                    ) : isCompleted ? (
                      <TaskCheckmarkFilled className="h-8 w-8 text-success-05" />
                    ) : (
                      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-grey-03 text-white">
                        <Time className="h-4 w-4 text-grey-06" />
                      </span>
                    )}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-[#231F20]">
                      <div className="flex items-center gap-2 py-1 text-sm leading-none text-grey-08">
                        {competence?.competenceCategoryColorCode && (
                          <span
                            style={{
                              backgroundColor:
                                competence?.competenceCategoryColorCode,
                            }}
                            className={`h-1.5 w-1.5 min-w-1.5 rounded-full`}
                          ></span>
                        )}
                        <p className="whitespace-normal leading-[1.3]">
                          {competence?.name}
                        </p>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-5 text-[#7D7D81]">
                      {competence.code && <span>{competence.code}</span>}

                      <span>
                        {competence.isMandatory
                          ? t(
                              'pages.follow_up_users.profile_competence_mandatory'
                            )
                          : t(
                              'pages.follow_up_users.profile_competence_optional'
                            )}
                      </span>
                    </div>
                  </div>

                  <div className="ml-auto flex min-w-max items-center gap-3">
                    <div className="flex items-center gap-1">
                      {competence.assignmentMethod && (
                        <div className="text-sm text-grey-06">
                          {competence.assignmentMethod === 'manual'
                            ? t('pages.follow_up_teams.manual')
                            : competence.assignmentMethod === 'exemption'
                            ? t(
                                'pages.follow_up_users.assign_comp_user.exemption'
                              )
                            : competence.assignmentMethod === 'certificate' &&
                              t(
                                'pages.manage_certificates.example.certificate'
                              )}
                        </div>
                      )}
                      {competence.achievedAt && (
                        <div className="text-sm text-grey-06">
                          {competence.assignmentMethod && (
                            <span className="mr-1 text-lg">∙</span>
                          )}
                          {format(
                            new Date(competence.achievedAt),
                            'dd MMM yyyy'
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex min-w-[130px] items-center">
                      <Tag
                        size="sm"
                        title={
                          hasExpired
                            ? t('pages.follow_up_users.profile_status_expired')
                            : isRevoked
                            ? t(
                                'pages.follow_up_users.competence_status_revoked'
                              )
                            : isDenied
                            ? t(
                                'pages.follow_up_users.competence_status_denied'
                              )
                            : isExempted
                            ? t(
                                'pages.follow_up_users.competence_status_exempted'
                              )
                            : isCompleted
                            ? t(
                                'pages.follow_up_users.competence_status_completed'
                              )
                            : competence.progress === 0
                            ? t(
                                'pages.follow_up_users.competence_status_not_started'
                              )
                            : t(
                                'pages.follow_up_users.competence_status_not_completed'
                              )
                        }
                        variant={
                          hasExpired
                            ? 'error'
                            : isRevoked
                            ? 'error'
                            : isDenied
                            ? 'error'
                            : isExempted
                            ? 'primary'
                            : isCompleted
                            ? 'success'
                            : 'warning'
                        }
                      />
                    </div>
                    <CompetenceActionMenu
                      competence={competence}
                      onAction={onCompetenceAction}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {maxPage > 1 && (
            <div className="flex w-full items-center justify-between px-4">
              <div>
                <PaginationNew
                  setOffset={setOffset}
                  offset={offset}
                  count={count}
                  page={page}
                  maxPage={maxPage}
                />
              </div>
              <div className="flex items-center">
                <div className="ml-2 hidden items-center gap-1 lg:flex">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.go_to_page')}
                  </p>
                  <InputNumber
                    mode="integer"
                    value={page}
                    onChange={(value: any) => {
                      if (value > 0) {
                        setOffset((value - 1) * count);
                      }
                    }}
                    min={1}
                    max={maxPage}
                    className="max-w-[50px]"
                  />
                </div>
                <div className="ml-2 flex items-center gap-1">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.show')}
                  </p>
                  <div>
                    <Listbox
                      value={showType}
                      onChange={(newShowType) => {
                        setShowType(newShowType);
                        setCount(newShowType?.value);
                        setOffset(
                          Math.floor(offset / newShowType?.value) *
                            newShowType?.value
                        );
                      }}
                    >
                      {({ open }) => (
                        <div>
                          <Listbox.Button
                            className={clsx(
                              'flex items-center justify-between',
                              'rounded-md border border-solid outline outline-2',
                              'min-h-10 w-full min-w-[70px] px-2 text-base',
                              {
                                'border-secondary-04 bg-white outline-secondary-01':
                                  open,
                                'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                  !open,
                              }
                            )}
                          >
                            <span className="mr-2 block truncate">
                              {!showType?.label ? '' : showType?.label}
                            </span>
                            <ChevronDownFilled
                              className={clsx(
                                'h-5 w-5',
                                'transition-transform',
                                open && 'rotate-180'
                              )}
                            />
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition-opacity"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="listbox relative z-10">
                              <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                {showOptions.map((option, optionIndex) => (
                                  <Listbox.Option
                                    key={optionIndex}
                                    className={({ active }) =>
                                      clsx(
                                        'rounded-md px-3 py-2 text-base text-black',
                                        active && 'bg-grey-transparent-02'
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected }) => (
                                      <span
                                        className={clsx(
                                          'block',
                                          selected && 'font-medium'
                                        )}
                                      >
                                        <span>{option?.label}</span>
                                      </span>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function FollowUpUserCompetencies() {
  const { userId, organisationId } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { setSubPage } = useContext(NavContext);
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const profileUserId = userId;
  const canAssignProfiles =
    Math.max(authUser?.role || 0, authUser?.activeOrganisationRole || 0) >= 50;
  const canAssignCompetences =
    Math.max(authUser?.role || 0, authUser?.activeOrganisationRole || 0) >= 40;

  const [student, setStudent] = useState<any>(null);
  const [isAssignProfilesOpen, setIsAssignProfilesOpen] = useState(false);
  const [isAssignCompetenceOpen, setIsAssignCompetenceOpen] = useState(false);
  const [isCompetenceActionOpen, setIsCompetenceActionOpen] = useState(false);
  const [activeCompetence, setActiveCompetence] = useState<any>(null);
  const [competenceAction, setCompetenceAction] = useState<
    'assign' | 'revoke' | 'history'
  >('assign');

  const [expandedUnProfile, setExpandedUnProfile] = useState(false);

  const [profileInpFilter, setProfileInpFilter] = useState('');
  const [profileContentRange, setProfileContentRange] = useState(0);
  const [expandedProfileIds, setExpandedProfileIds] = useState<
    Set<string | number>
  >(new Set<string | number>());

  const { setShowNotification } = useContext(NotificationContext);

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useGet('/memberdetails', {
    organisationId,
    userId,
    onSuccess: (result: any) => {
      setStudent(result?.data?.[0] || {});
    },
  });

  const profileListResult: any = useGet(
    '/organisationstudentprofessiondetails',
    {
      organisationId,
      userId: profileUserId,
      count: 1000,
      sort: 'professionName',
      q: parseFilter(profileInpFilter),
      enabled: !!organisationId && !!profileUserId,
      onSuccess: (result: any) => {
        const contentRange = result?.headers?.['content-range']
          ?.split(' ')?.[1]
          ?.split('/');

        setProfileContentRange(
          Number(contentRange?.[1] || result?.data?.length || 0)
        );
      },
    }
  );

  const [offsetUnProf, setOffsetUnProf] = useState(0);
  const [resultUnProf, setResultUnProf] = useState<any>(null);
  const [countUnProf, setCountUnProf] = useState(10);
  const [loadedUnProf, setLoadedUnProf] = useState(true);
  const [loadedUnProfLength, setLoadedUnProfLength] = useState(false);
  const [unprofiledCompetenceLength, setUnprofiledCompetenceLength] =
    useState(0);
  useGet('/studentunprofiledcompetencedetails', {
    organisationId,
    userId: profileUserId,
    count: 0,
    offset: offsetUnProf,
    onSuccess: (result: any) => {
      const contentRange = result?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/')[1];
      setUnprofiledCompetenceLength(contentRange);
      setLoadedUnProfLength(true);
    },
  });

  useGet('/studentunprofiledcompetencedetails', {
    organisationId,
    userId: profileUserId,
    count: countUnProf,
    offset: offsetUnProf,
    enabled: expandedUnProfile && !!organisationId && !!profileUserId,
    onSuccess: (result: any) => {
      setLoadedUnProf(true);
      setResultUnProf(result || {});
    },
  });
  useEffect(() => {
    if (!expandedUnProfile) return;
    setLoadedUnProf(false);
  }, [countUnProf, expandedUnProfile, offsetUnProf]);

  const profileRows = profileListResult?.data?.data || [];

  const [selectedRow, setSelectedRow] = useState<Set<string | number>>(
    new Set<string | number>()
  );
  const [selectMode, setSelectMode] = useState(true);

  const [isOpenDelModal, setIsOpenDelModal] = useState(false);

  const handleItemSelect = (key: any) => {
    const newSelected = new Set(selectedRow);
    if (
      (newSelected.has(key) && selectMode) ||
      (!newSelected.has(key) && !selectMode)
    ) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedRow(newSelected);
  };

  const getProfileStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          label: t('pages.follow_up_users.profile_status_completed'),
          variant: 'success',
        };
      case 'active':
        return {
          label: t('pages.follow_up_users.profile_status_active'),
          variant: 'primary',
        };
      case 'revoked':
      case 'expired':
      case 'archived':
        return {
          label: t(`pages.follow_up_users.profile_status_${status}`),
          variant: 'error',
        };
      case 'assigned':
      default:
        return {
          label: t('pages.follow_up_users.profile_status_assigned'),
          variant: 'warning',
        };
    }
  };

  if (!student) return null;

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const deleteCompetencyProfile = () => {
    [...selectedRow]?.map((professionId: any) => {
      axios
        .delete(`${config.backend}/studentprofession/${professionId}`)
        .then(() => {
          setSelectedRow(new Set<string | number>());
          profileListResult.refetch();
          setIsOpenDelModal(false);
        });
    });
  };

  const openCompetenceAction = (
    competence: any,
    action: 'assign' | 'revoke' | 'history'
  ) => {
    document.body.style.overflow = 'hidden';
    setActiveCompetence(competence);
    setCompetenceAction(action);
    setIsCompetenceActionOpen(true);
  };

  const downloadReport = async (professionId: any) => {
    try {
      const result: any = await axios.post(`${config.teoreport}/report`, {
        download: true,
        lang: authUser?.lang,
        organisationId: organisationId,
        formType: 'competenceOverview',
        professionIds: [professionId],
        userIds: [profileUserId],
      });

      if (result?.data?.link) {
        downloadLink(result.data.link);
      }
      setShowNotification({
        message: t('pages.report_page.report_download_message'),
        delay: 5000,
      });
    } catch (error: any) {
      console.error('Report request failed:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('pages.report.report_error');

      setShowNotification({
        message: errorMessage,
        delay: 5000,
      });
    }
  };

  return (
    <div className="max-w-6xl flex-col pb-8">
      {isMobile && (
        <MobileSubPageMenu
          backFn={() => navigate(`${basePath + '?tab=users'}`)}
        />
      )}
      <BreadCrumbsDetails
        prevPathname={basePath + '?tab=users'}
        prevName={t('breadcrumbs.USERS')}
        pathname={window.location.pathname}
        name={student.username}
      />
      <div className="pb-2 md:pt-6">
        {isMobile ? (
          <div className="flex w-full flex-col gap-4">
            <div className="flex items-center">
              <PersonAvatar
                inline
                id={student?.id}
                name={student?.username}
                imageSrc={student?.picture}
              ></PersonAvatar>
              <h1 className="break-all pb-1.5 text-lg font-semibold text-black sm:text-xl">
                {student?.username}
              </h1>
            </div>

            <div className="flex w-full flex-col justify-between gap-4 xl:mx-4 xl:flex-row">
              <div className="flex flex-col content-center">
                <div className="flex flex-wrap content-center text-xs text-[#919193]">
                  <p className="mr-2 border-r border-[#919193] pr-2">
                    {t(parseRole(student?.role))}
                  </p>
                  <span>
                    <Link
                      className="inline-block pr-2 text-xs text-tint-blue hover:underline"
                      to="../#"
                    >
                      {student?.email}
                    </Link>
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <>
                  {canAssignProfiles && (
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => {
                        document.body.style.overflow = 'hidden';
                        setIsAssignProfilesOpen(true);
                      }}
                    >
                      {t('pages.profile_assignment.user_action')}
                    </Button>
                  )}
                  {canAssignCompetences && (
                    <Button
                      size="md"
                      onClick={() => {
                        document.body.style.overflow = 'hidden';
                        setIsAssignCompetenceOpen(true);
                      }}
                    >
                      {t('pages.follow_up_users.manual_assign_action')}
                    </Button>
                  )}
                </>
              </div>
            </div>
          </div>
        ) : (
          <PageHeader to={basePath + '?tab=users'} ariaLabel={'placeholder'}>
            <div className="flex w-full items-center">
              <div className="flex items-center">
                <PersonAvatar
                  inline
                  id={student?.id}
                  name={student?.username}
                  imageSrc={student?.picture}
                ></PersonAvatar>
              </div>

              <div className="mx-2 flex w-full flex-col justify-between gap-4 xl:mx-4 xl:flex-row">
                <div className="flex flex-col content-center">
                  <h1 className="break-all pb-1.5 text-lg font-semibold text-black sm:text-xl">
                    {student?.username}
                  </h1>
                  <div className="flex flex-wrap content-center text-xs text-[#919193]">
                    <p className="mr-2 border-r border-[#919193] pr-2">
                      {t(parseRole(student?.role))}
                    </p>
                    <span>
                      <Link
                        className="inline-block pr-2 text-xs text-tint-blue hover:underline"
                        to="../#"
                      >
                        {student?.email}
                      </Link>
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <>
                    {canAssignProfiles && (
                      <Button
                        variant="outline"
                        size="md"
                        onClick={() => {
                          document.body.style.overflow = 'hidden';
                          setIsAssignProfilesOpen(true);
                        }}
                      >
                        {t('pages.profile_assignment.user_action')}
                      </Button>
                    )}
                    {canAssignCompetences && (
                      <Button
                        size="md"
                        onClick={() => {
                          document.body.style.overflow = 'hidden';
                          setIsAssignCompetenceOpen(true);
                        }}
                      >
                        {t('pages.follow_up_users.manual_assign_action')}
                      </Button>
                    )}
                  </>
                </div>
              </div>
            </div>
          </PageHeader>
        )}
      </div>

      <>
        <div className="flex w-full flex-col sm:flex-row sm:items-center">
          <div className="pb-4 pt-5 sm:w-1/2">
            <InputText
              value={profileInpFilter}
              onChange={(ev) => setProfileInpFilter(ev.target.value)}
              Icon={Search}
              placeholder={t('pages.follow_up_users.search_profile')}
            />
          </div>
          {selectedRow?.size > 0 && (
            <div className="mb-4 ml-auto flex w-auto justify-end sm:mb-0">
              <Button
                onClick={() => {
                  setIsOpenDelModal(true);
                }}
                size="sm"
                disabled={selectedRow?.size < 1}
                variant="outline"
                className="!text-error-04"
              >
                {t('pages.manage_competencies.delete_button')}
              </Button>
            </div>
          )}
        </div>

        {profileListResult?.isLoading || !loadedUnProfLength ? (
          <div className="mb-3 rounded-lg border border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
            {t('pages.follow_up_users.profiles_loading')}
          </div>
        ) : (
          profileRows.length === 0 &&
          unprofiledCompetenceLength == 0 && (
            <EmptyTable
              template={1}
              text={t('empty_tenplate.profile_list')}
              btn_text={t('pages.profile_assignment.team_action')}
              btnClick={() => {
                document.body.style.overflow = 'hidden';
                setIsAssignProfilesOpen(true);
              }}
            />
          )
        )}
        {profileRows.length > 0 && (
          <div className="mb-3 flex flex-col gap-3">
            {profileRows.map((profile: any) => {
              const status = getProfileStatus(profile.status);
              const profileKey =
                profile.id || `${profile.userId}-${profile.professionId}`;
              const isExpanded = expandedProfileIds.has(profileKey);

              return (
                <div
                  key={profileKey}
                  className={`rounded-lg border border-grey-02 bg-white ${
                    isExpanded ? 'overflow-visible' : 'overflow-hidden'
                  }`}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-t-lg bg-[#F6F6F7] px-4 py-4 text-left"
                    onClick={() => {
                      setExpandedProfileIds((current) => {
                        const next = new Set(current);

                        if (next.has(profileKey)) {
                          next.delete(profileKey);
                        } else {
                          next.add(profileKey);
                        }

                        return next;
                      });
                    }}
                  >
                    <span className="grid w-full grid-cols-[28px_1fr] items-center gap-3 md:grid-cols-[28px_1fr_190px]">
                      <span
                        className="min-w-0"
                        onClick={(ev) => {
                          ev?.preventDefault?.();
                          ev?.stopPropagation?.();
                        }}
                      >
                        <Checkbox
                          id="selectedRow"
                          ariaLabel="test"
                          onChange={() => handleItemSelect(profile.id)}
                          checked={selectedRow.has(profile.id)}
                          key={profile.id + '_' + selectedRow.has(profile.id)}
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-base font-semibold text-[#231F20]">
                            {profile.professionName}
                          </span>
                          <Tag
                            size="sm"
                            title={status.label}
                            variant={status.variant as any}
                          />
                        </span>
                        <span className="mt-1 block text-xs text-[#7D7D81]">
                          {profile.completedCompetences || 0}/
                          {profile.requiredCompetences || 0}{' '}
                          {t('pages.follow_up_users.profile_competences')} ·{' '}
                          {profile.isQualified
                            ? t(
                                'pages.follow_up_users.profile_status_completed'
                              )
                            : t(
                                'pages.follow_up_users.profile_status_not_qualified'
                              )}
                        </span>
                      </span>
                      <span className="col-start-2 flex md:col-start-auto md:justify-end">
                        <ProfileProgress value={profile.progress} />
                      </span>
                    </span>

                    <div className="flex flex h-7 min-w-0 items-center justify-center gap-4 text-[#7D7D81]">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="!px-2 !py-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadReport(profile.professionId);
                        }}
                      >
                        <Download className="h-5 w-5" />
                      </Button>
                      <ChevronRight
                        className={`h-5 w-5 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExpanded ? 'rotate-90' : 'rotate-0'
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`relative z-[1] grid transition-[grid-template-rows] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div
                      className={
                        isExpanded ? 'overflow-visible' : 'overflow-hidden'
                      }
                    >
                      <div
                        className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExpanded
                            ? 'translate-y-0 opacity-100'
                            : '-translate-y-2 opacity-0'
                        }`}
                      >
                        {isExpanded && (
                          <ProfileCompetenceRows
                            organisationId={organisationId}
                            userId={profile.userId}
                            professionId={profile.professionId}
                            onCompetenceAction={openCompetenceAction}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {unprofiledCompetenceLength > 0 && (
          <div
            className={`rounded-lg border border-grey-02 bg-[#F6F6F7] ${
              expandedUnProfile ? 'overflow-visible' : 'overflow-hidden'
            }`}
          >
            <button
              type="button"
              className="grid w-full grid-cols-[28px_1fr] items-center gap-3 bg-[#F6F6F7] px-4 py-4 text-left md:grid-cols-[28px_1fr_190px]"
              onClick={() => {
                setExpandedUnProfile(!expandedUnProfile);
              }}
            >
              <span className="flex h-7 w-7 items-center justify-center text-[#7D7D81]">
                <ChevronRight
                  className={`h-5 w-5 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    expandedUnProfile ? 'rotate-90' : 'rotate-0'
                  }`}
                />
              </span>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-base font-semibold text-[#231F20]">
                    {t('pages.follow_up_users.outside_profile')}
                  </span>
                </span>
                <span className="mt-1 block text-xs text-[#7D7D81]">
                  {t('pages.follow_up_users.manually_assigned')}
                </span>
              </span>
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                expandedUnProfile ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div
                className={
                  expandedUnProfile ? 'overflow-visible' : 'overflow-hidden'
                }
              >
                <div
                  className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    expandedUnProfile
                      ? 'translate-y-0 opacity-100'
                      : '-translate-y-2 opacity-0'
                  }`}
                >
                  <UnProfileCompetenceRows
                    setOffset={setOffsetUnProf}
                    offset={offsetUnProf}
                    setCount={setCountUnProf}
                    count={countUnProf}
                    result={resultUnProf}
                    loadedUnProf={loadedUnProf}
                    onCompetenceAction={openCompetenceAction}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </>
      {isAssignProfilesOpen && organisationId && userId && (
        <AssignProfiles
          isOpen={isAssignProfilesOpen}
          onClose={() => {
            document.body.style.overflow = 'unset';
            setIsAssignProfilesOpen(false);
          }}
          onSaved={() => profileListResult.refetch()}
          organisationId={organisationId}
          targetId={userId}
          targetName={student.username}
          targetType="user"
        />
      )}
      {isAssignCompetenceOpen && organisationId && userId && (
        <AddCompetenceUser
          isOpen={isAssignCompetenceOpen}
          onAssigned={() => {
            void queryClient.invalidateQueries({
              queryKey: ['/studentprofessioncompetencedetails'],
            });
            void queryClient.invalidateQueries({
              queryKey: ['/organisationstudentprofessiondetails'],
            });
            void queryClient.invalidateQueries({
              queryKey: ['/studentunprofiledcompetencedetails'],
            });
          }}
          onClose={() => {
            document.body.style.overflow = 'unset';
            setIsAssignCompetenceOpen(false);
          }}
          organisationId={organisationId}
          studentName={student.username}
          userId={Number(userId)}
          activeCompetence={null}
        />
      )}
      {isCompetenceActionOpen &&
        organisationId &&
        userId &&
        activeCompetence && (
          <AddCompetenceUser
            isOpen={isCompetenceActionOpen}
            onAssigned={() => {
              void queryClient.invalidateQueries({
                queryKey: ['/studentprofessioncompetencedetails'],
              });
              void queryClient.invalidateQueries({
                queryKey: ['/organisationstudentprofessiondetails'],
              });
              void queryClient.invalidateQueries({
                queryKey: ['/studentunprofiledcompetencedetails'],
              });
            }}
            onClose={() => {
              document.body.style.overflow = 'unset';
              setIsCompetenceActionOpen(false);
              setActiveCompetence(null);
            }}
            organisationId={organisationId}
            studentName={student.username}
            userId={Number(userId)}
            activeCompetence={activeCompetence}
            action={competenceAction}
          />
        )}
      {isOpenDelModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDelModal(false)}
          setIsOpenDeleteModal={setIsOpenDelModal}
          title={t(
            selectedRow?.size > 1
              ? 'delete_modal.user_profiles.title_m'
              : 'delete_modal.user_profiles.title'
          )}
          heading={t(
            selectedRow?.size > 1
              ? 'delete_modal.user_profiles.heading_m'
              : 'delete_modal.user_profiles.heading'
          )}
          text={t(
            selectedRow?.size > 1
              ? 'delete_modal.user_profiles.text_m'
              : 'delete_modal.user_profiles.text'
          )}
          handleDelete={() => deleteCompetencyProfile()}
        />
      )}
    </div>
  );
}

export default FollowUpUserCompetencies;
